
import { Driver, Order, SystemConfig } from '../../../types';
import { calculateDriverMinutesToHub } from './metrics';

export interface FleetSlot {
    driver: Driver;
    delayMins: number;
    driverId: string;
    isAvailable: boolean;
    hasActiveAcceptedRoute: boolean;
    reason: 'idle' | 'returning' | 'busy_en_route' | 'allocated_pending';
    requiresOvertimeApproval?: boolean;
}

/**
 * v2025.02.08: getFleetAvailability - REGRAS DE DISPONIBILIDADE CORRIGIDAS
 * - Disponibilidade Presumida: motoboy sem pickupConfirmed ativo está disponível para mudanças
 * - Disponibilidade Preditiva: calcula retorno real de quem está na rua (pickupConfirmed)
 * - Soberania da Escala: Se 'ativo' no painel e no horário, está disponível
 * - Contexto de Hora Extra: Se no dia de trabalho mas fora do turno, sugere com alerta
 */
export const getFleetAvailability = (
    drivers: Driver[],
    allOrders: Order[],
    hubLat: number,
    hubLng: number,
    config: SystemConfig
): FleetSlot[] => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Filtra apenas motoboys que não foram desativados administrativamente
    const candidates = drivers.filter(d => d.status !== 'inactive');

    const slots: FleetSlot[] = candidates.map(d => {
        // REGRA DE DISPONIBILIDADE PRESUMIDA:
        // Considera apenas pedidos que realmente estão em rota ativa (pickupConfirmed = true)
        // Pedidos 'assigned' sem pickupConfirmed não bloqueiam o motoboy
        const myOrders = allOrders.filter(o => o.driverId === d.id && (o.status === 'assigned' || o.status === 'dispatched'));
        const trulyActiveOrders = myOrders.filter(o => o.pickupConfirmed === true);
        const hasActiveAcceptedRoute = trulyActiveOrders.length > 0;
        
        // Calcula retorno baseado apenas em pedidos realmente ativos
        const returnMins = calculateDriverMinutesToHub(d, trulyActiveOrders, hubLat, hubLng, config.fleetConfig.serviceTimeMinutes);

        // Verificação de Escala (SaaS Logic)
        const isWorkDay = d.schedule?.activeDays.includes(currentDay);
        const activeShift = d.schedule?.shifts.find(s => {
            const [startH, startM] = s.start.split(':').map(Number);
            const [endH, endM] = s.end.split(':').map(Number);
            const startTotal = startH * 60 + startM;
            const endTotal = endH * 60 + endM;
            return currentMinutes >= startTotal && currentMinutes <= endTotal;
        });

        const isInShift = !!activeShift;

        let reason: FleetSlot['reason'] = 'idle';
        let isAvailable = false;
        let effectiveDelay = 0;
        let requiresOvertimeApproval = false;

        // 1. DETERMINAÇÃO DE STATUS BASE
        // v57.9: Motorista ativo no painel = disponível (Soberania do Operador)
        // Horário/dia de trabalho apenas determina se precisa aprovação de hora extra
        if (hasActiveAcceptedRoute) {
            // Disponibilidade preditiva: motorista em rota ativa pode ser candidato FUTURO.
            // Ele nao fica elegivel para saida imediata, mas entra no arbitro com atraso real.
            isAvailable = true;
            reason = 'busy_en_route';
            effectiveDelay = Math.max(1, returnMins);
        } else {
            // Motorista ativo sem rota ativa = disponível
            isAvailable = true;

            if (myOrders.length > 0) {
                reason = 'allocated_pending';
                effectiveDelay = 0;
            } else if (returnMins > 0) {
                reason = 'returning';
                effectiveDelay = returnMins;
            } else {
                reason = 'idle';
                effectiveDelay = 0;
            }

            // v57.9: Alerta de Hora Extra se fora do dia de trabalho ou fora do turno
            if (!isWorkDay || !isInShift) {
                requiresOvertimeApproval = true;
            }
        }

        return {
            driver: d,
            driverId: d.id,
            delayMins: effectiveDelay,
            isAvailable,
            hasActiveAcceptedRoute,
            reason,
            requiresOvertimeApproval // Adicionado para avisar o Brain/UI
        };
    });

    return slots
        .filter(s => s.isAvailable)
        .sort((a, b) => {
            // Prioriza quem está no horário normal vs hora extra
            if (a.requiresOvertimeApproval !== b.requiresOvertimeApproval) {
                return a.requiresOvertimeApproval ? 1 : -1;
            }
            return a.delayMins - b.delayMins;
        });
};
